import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Check } from "lucide-react";
import { API, trackEvent, formatApiError, QUIZ_URL } from "@/lib/api";

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");

  const subscribe = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      const { data } = await axios.post(`${API}/subscribe`, { email, source: "footer" });
      setStatus("done");
      setMessage(data.message);
      setEmail("");
      trackEvent("subscribed", { source: "footer" });
    } catch (err) {
      setStatus("error");
      setMessage(formatApiError(err));
    }
  };

  return (
    <footer className="border-t border-white/10 bg-obsidian">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-2.5 mb-5">
              <span className="w-3.5 h-3.5 bg-volt" />
              <span className="font-display font-extrabold tracking-tight text-xl">
                WIRED<span className="text-volt">2</span>WIN
              </span>
            </div>
            <p className="text-white/60 text-sm leading-relaxed max-w-sm mb-8">
              Mental game training for golfers who refuse to leave strokes on the table. Get one actionable mindset drill in your inbox every week.
            </p>
            {status === "done" ? (
              <div data-testid="footer-subscribe-success" className="flex items-center gap-2 text-volt font-semibold text-sm">
                <Check className="w-4 h-4" /> {message}
              </div>
            ) : (
              <form onSubmit={subscribe} className="flex max-w-sm" data-testid="footer-subscribe-form">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  data-testid="footer-email-input"
                  aria-label="Email address"
                  className="flex-1 bg-white/5 border border-white/15 px-4 py-3 text-sm placeholder:text-white/30 focus:outline-none focus:border-volt transition-colors"
                />
                <button
                  type="submit"
                  disabled={status === "loading"}
                  data-testid="footer-subscribe-button"
                  className="bg-volt text-obsidian px-4 hover:bg-white transition-colors disabled:opacity-50"
                  aria-label="Subscribe"
                >
                  <ArrowRight className="w-5 h-5" />
                </button>
              </form>
            )}
            {status === "error" && (
              <p data-testid="footer-subscribe-error" className="text-red-400 text-xs mt-2">{message}</p>
            )}
          </div>

          <div className="lg:col-span-3 lg:col-start-7">
            <p className="text-xs tracking-[0.25em] uppercase font-bold text-white/40 mb-5">Explore</p>
            <ul className="space-y-3 text-sm">
              <li><a data-testid="footer-link-quiz" href={QUIZ_URL} className="text-white/70 hover:text-volt transition-colors">The Assessment</a></li>
              <li><Link data-testid="footer-link-about" to="/about" className="text-white/70 hover:text-volt transition-colors">About</Link></li>
              <li><Link data-testid="footer-link-resources" to="/resources" className="text-white/70 hover:text-volt transition-colors">Resources</Link></li>
              <li><Link data-testid="footer-link-contact" to="/contact" className="text-white/70 hover:text-volt transition-colors">Contact</Link></li>
              <li><Link data-testid="footer-link-members" to="/members" className="text-white/70 hover:text-volt transition-colors">Member Assets</Link></li>
              <li><Link data-testid="footer-link-redeem" to="/redeem" className="text-white/70 hover:text-volt transition-colors">Redeem a Code</Link></li>
              <li><Link data-testid="footer-link-coaches" to="/coaches" className="text-white/70 hover:text-volt transition-colors">For Coaches</Link></li>
              <li><Link data-testid="footer-link-women" to="/women" className="text-white/70 hover:text-volt transition-colors">Women</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <p className="text-xs tracking-[0.25em] uppercase font-bold text-white/40 mb-5">Legal</p>
            <ul className="space-y-3 text-sm">
              <li><Link data-testid="footer-link-privacy" to="/privacy" className="text-white/70 hover:text-volt transition-colors">Privacy Policy</Link></li>
              <li><Link data-testid="footer-link-terms" to="/terms" className="text-white/70 hover:text-volt transition-colors">Terms of Service</Link></li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between gap-4 text-xs text-white/40">
          <p>© {new Date().getFullYear()} Wired 2 Win Golf. All rights reserved.</p>
          <p>Wired for the walk up 18.</p>
        </div>
      </div>
    </footer>
  );
};
